/* global React, Icon, Card, Button, Modal, Segmented, toast, fmt */
// ============================================================
// PLUMME 3 — Privat økonomi · Fremtid og Familie
// Setter sammen scenariomotoren (privatokonomi-scenarier.jsx) til
// en livsplanlegger. Utgangspunktet og startformuen hentes fra
// readmodel. Plan, mål og livshendelser lagres via orkestratoren.
// ============================================================
const { useState: pfUseState, useEffect: pfUseEffect, useMemo: pfUseMemo } = React;

const pfAvatar = () => (window.__resources && window.__resources.superPlummaHero) || 'src/img/SuperPlumma.png';

// ---------------- Fremtid ----------------
function PoFuture({ accounts = [], name, onAsk, onSavePlanning, savedEvents = [], savedGoals = [], savedPlan = null, startNet = 0, suggestedMonthlySave = 0 }) {
  const Sec = window.PoSecHead;
  const [horizon, setHorizon] = pfUseState('5');
  const [scenario, setScenario] = pfUseState('base');
  const [scenarioParams, setScenarioParams] = pfUseState({});
  const defaultPlan = { currentAge: '', drawMonthly: '', extraDebt: 0, monthlySave: Math.max(0, Math.round(suggestedMonthlySave)), ret: '', retireAge: '', targetNet: 0, yearlyNeed: 0 };
  const [plan, setPlan] = pfUseState(() => savedPlan || defaultPlan);
  const [planOpen, setPlanOpen] = pfUseState(false);
  const [scenOpen, setScenOpen] = pfUseState(false);
  const [goals, setGoals] = pfUseState(() => savedGoals);
  const [goalEdit, setGoalEdit] = pfUseState(null);
  const [goalDelete, setGoalDelete] = pfUseState(null);
  const [openGoal, setOpenGoal] = pfUseState(null);
  const [events, setEvents] = pfUseState(() => savedEvents);
  const [eventEdit, setEventEdit] = pfUseState(null);

  pfUseEffect(() => {
    setPlan(savedPlan || { currentAge: '', drawMonthly: '', extraDebt: 0, monthlySave: Math.max(0, Math.round(suggestedMonthlySave)), ret: '', retireAge: '', targetNet: 0, yearlyNeed: 0 });
  }, [savedPlan, suggestedMonthlySave]);
  pfUseEffect(() => { setGoals(savedGoals); }, [savedGoals]);
  pfUseEffect(() => { setEvents(savedEvents); }, [savedEvents]);

  const first = String(name || '').split(' ')[0];
  const definition = PF_SCEN.find((item) => item.key === scenario) || PF_SCEN[0];
  const params = { ...(definition.defaults || {}), ...(scenarioParams[scenario] || {}) };
  const setParam = (key, value) => setScenarioParams((current) => ({ ...current, [scenario]: { ...params, [key]: value } }));
  const scen = definition.compute(plan, params);

  const effectiveRetireAge = plan.retireAge + scen.retire;
  const retireMonth = Math.max(0, (effectiveRetireAge - plan.currentAge) * 12);
  const baseMonthly = plan.monthlySave + plan.extraDebt;
  const effectiveMonthly = Math.max(0, baseMonthly + scen.save);
  const activeEvents = events.filter((event) => event.active !== false);
  const months = horizon === 'pensjon' ? retireMonth + 240 : horizon === '1' ? 12 : horizon === '5' ? 60 : 120;
  const points = pfSamplePoints(pfProjectFull(startNet, effectiveMonthly, months, plan.ret, activeEvents, retireMonth, plan.drawMonthly), months);
  const net5 = pfProjectFull(startNet, effectiveMonthly, 60, plan.ret, activeEvents, retireMonth, plan.drawMonthly)[60];
  const base5 = pfProject(startNet, baseMonthly, 60, plan.ret)[60];

  const lifeProjection = pfProjectFull(startNet, effectiveMonthly, retireMonth + 600, plan.ret, activeEvents, retireMonth, plan.drawMonthly);
  const retireNet = lifeProjection[retireMonth] || 0;
  let depletedIndex = -1;
  for (let month = retireMonth + 1; month < lifeProjection.length; month += 1) { if (lifeProjection[month] <= 0) { depletedIndex = month; break; } }
  const depletedAge = depletedIndex < 0 ? null : Math.floor(plan.currentAge + depletedIndex / 12);
  const targetIndex = plan.targetNet ? lifeProjection.slice(0, retireMonth + 1).findIndex((value) => value >= plan.targetNet) : -1;
  const targetText = !plan.targetNet ? t('pkn.fut.notSet') : targetIndex < 0 ? t('pkn.fut.notBeforeRetirement') : pfDateFull(targetIndex);

  const eventsSave = activeEvents.filter((event) => event.start <= 24).reduce((sum, event) => sum + (event.monthly || 0), 0);
  const capacitySave = scen.save + eventsSave;
  const weightSum = goals.reduce((sum, goal) => sum + goal.monthly, 0) || 1;
  const computedGoals = goals.map((goal) => {
    const share = capacitySave * (goal.monthly / weightSum);
    const goalMonths = pfGoalMonths(goal, share);
    const baseMonths = pfGoalMonths(goal, 0);
    const nextAt = goal.nextMark?.at || goal.target;
    const markMonths = pfGoalMonths({ ...goal, target: nextAt }, share);
    return {
      baseMonths,
      delta: Number.isFinite(baseMonths) && Number.isFinite(goalMonths) ? baseMonths - goalMonths : 0,
      goal,
      left: Math.max(0, goal.target - goal.saved),
      markMonths,
      months: goalMonths,
      pct: Math.min(100, Math.round(goal.saved / goal.target * 100)),
      onTrack: goal.saved >= goal.target || goal.monthly + share > 0,
      planStatus: goal.saved >= goal.target ? t('pkn.fut.status.reached') : goal.monthly + share > 0 ? t('pkn.fut.status.planned') : t('pkn.fut.status.noSaving'),
      share,
    };
  });
  const onTrack = computedGoals.filter((item) => item.onTrack).length;
  const totalSaved = goals.reduce((sum, goal) => sum + goal.saved, 0);
  const totalTarget = goals.reduce((sum, goal) => sum + goal.target, 0) || 1;
  const totalPct = goals.length ? Math.round(totalSaved / totalTarget * 100) : 0;

  const factors = [
    { name: t('pkn.fut.factor.progress'), note: t('pkn.fut.factor.progressNote', { pct: totalPct }), pct: totalPct / 100 },
    { name: t('pkn.fut.factor.onPlan'), note: t('pkn.fut.factor.onPlanNote', { count: onTrack, total: goals.length }), pct: goals.length ? onTrack / goals.length : 0 },
    { name: t('pkn.fut.factor.autoBalance'), note: t('pkn.fut.factor.autoBalanceNote', { count: goals.filter((goal) => goal.savedSource === 'account').length, total: goals.length }), pct: goals.length ? goals.filter((goal) => goal.savedSource === 'account').length / goals.length : 0 },
  ];
  const goalScore = goals.length ? Math.round(factors.reduce((sum, factor) => sum + factor.pct, 0) / factors.length * 100) : 0;
  const priority = computedGoals.length ? [...computedGoals].sort((a, b) => Number(a.onTrack) - Number(b.onTrack) || (Number(b.months) || 0) - (Number(a.months) || 0))[0] : null;
  const milestones = computedGoals.filter((item) => Number.isFinite(item.markMonths)).sort((a, b) => a.markMonths - b.markMonths).slice(0, 4);

  // Livsplan bygges av det brukeren faktisk har lagt inn — mål, formuesmål og pensjon.
  const lifePlan = pfUseMemo(() => {
    const entries = computedGoals.filter((item) => Number.isFinite(item.months)).map((item) => ({ icon: item.goal.icon, label: t('pkn.fut.life.goalReached', { name: item.goal.name }), months: item.months, tint: item.goal.tint }));
    if (plan.targetNet && targetIndex >= 0) entries.push({ icon: 'trendUp', label: t('pkn.fut.life.netWorthPasses', { amount: fmt(plan.targetNet) }), months: targetIndex, tint: 'var(--success)' });
    entries.push({ icon: 'clock', label: t('pkn.fut.life.retireAge', { age: effectiveRetireAge }), months: retireMonth, tint: 'var(--plum-700)' });
    return entries.sort((a, b) => a.months - b.months);
  }, [computedGoals, effectiveRetireAge, plan.targetNet, retireMonth, targetIndex]);

  const impact = [
    { l: t('pkn.fut.impact.netWorth5'), tone: net5 >= base5 ? 'pos' : 'neg', v: fmt(Math.round(net5)) },
    { l: t('pkn.fut.impact.retireAge'), tone: scen.retire < 0 ? 'pos' : scen.retire > 0 ? 'neg' : 'neutral', v: t('pkn.fut.ageYears', { age: effectiveRetireAge }) },
    { l: t('pkn.fut.impact.savingToGoals'), tone: scen.save > 0 ? 'pos' : scen.save < 0 ? 'neg' : 'neutral', v: `${scen.save >= 0 ? '+' : ''}${t('pkn.fut.perMonth', { amount: fmt(scen.save) })}` },
    { l: t('pkn.fut.factor.onPlan'), tone: goals.length && onTrack >= goals.length * 0.7 ? 'pos' : 'neutral', v: goals.length ? t('pkn.fut.xOfY', { count: onTrack, total: goals.length }) : '—' },
    { l: t('pkn.fut.impact.debt'), tone: scen.debt && scen.debt.charAt(0) === '−' ? 'pos' : scen.debt ? 'neg' : 'neutral', v: scen.debt || t('pkn.fut.unchanged') },
  ];

  const saveGoal = (goal) => {
    const next = goals.some((item) => item.key === goal.key) ? goals.map((item) => (item.key === goal.key ? goal : item)) : [...goals, goal];
    setGoals(next);
    onSavePlanning?.({ goals: next });
    setGoalEdit(null);
    toast(t('pkn.fut.goalSaved'), 'success');
  };
  const saveEvent = (event) => {
    const next = events.some((item) => item.id === event.id) ? events.map((item) => (item.id === event.id ? event : item)) : [...events, event];
    setEvents(next);
    onSavePlanning?.({ lifeEvents: next });
    setEventEdit(null);
    toast(t('pkn.fut.eventSaved'), 'success');
  };

  if (!savedPlan) return <React.Fragment>
    <Card>
      <Sec icon="trendUp" title={t('po.yourFinancialFuture')} sub={t('pkn.fut.enterAssumptions')} />
      <div className="pf-life-empty">
        <Icon name="sliders" size={22} />
        <p>{t('pkn.fut.noForecast')}</p>
        <Button icon="plus" onClick={() => setPlanOpen(true)} data-help={t('helpTopics.scenario.body')} data-help-title={t('helpTopics.scenario.title')}>{t('pkn.fut.createPlan')}</Button>
      </div>
    </Card>
    {planOpen && <PoPlanEditor plan={plan} onSave={(next) => { setPlan(next); onSavePlanning?.({ plan: next }); setPlanOpen(false); }} onClose={() => setPlanOpen(false)} />}
  </React.Fragment>;

  return <React.Fragment>
    <Card>
      <Sec icon="trendUp" title={t('po.yourFinancialFuture')} sub={t('po.yourFinancialFutureSub')}
        right={<Segmented value={horizon} onChange={setHorizon} options={[{ label: t('pkn.fut.horizon1'), value: '1' }, { label: t('pkn.fut.horizon5'), value: '5' }, { label: t('pkn.fut.horizon10'), value: '10' }, { label: t('pkn.fut.horizonRetirement'), value: 'pensjon' }]} />} />
      {scenario !== 'base' && <div className="pf-scen-flag"><Icon name={definition.icon} size={14} />{t('sharedText.po.showingScenario')} <b>{definition.label}</b><button onClick={() => setScenario('base')}>{t('po.reset')}</button></div>}
      <div style={{ aspectRatio: '2.8 / 1', marginTop: 14, overflow: 'hidden' }}><PoLineChart points={points} /></div>
      <div className="pf-target"><Icon name="checkCircle" size={15} />{t('sharedText.po.desiredNetWealth')} <b>{plan.targetNet ? fmt(plan.targetNet) : t('pkn.fut.notSet')}</b> {t('pkn.fut.reaches')} <b>{targetText}</b></div>
      <div className="pf-target"><Icon name="clock" size={15} />{t('pkn.fut.expectedAtRetirement', { age: effectiveRetireAge })} <b>{fmt(Math.round(retireNet))}</b> — {depletedAge ? <React.Fragment>{t('sharedText.po.lastsUntilYouAre')} <b>{depletedAge} {t('sharedText.sd.transition.years')}</b></React.Fragment> : <React.Fragment>{t('sharedText.po.lasts')} <b>{t('po.forLife')}</b></React.Fragment>}</div>
      <div className="pf-story">
        <span className="pf-story-ava"><img src={pfAvatar()} alt="SuperPlumma" /></span>
        <div className="pf-story-body">
          <div className="pf-story-tag">{t('po.plummaReadsChart')}</div>
          <p>{scen.story()}</p>
        </div>
      </div>
    </Card>

    <Card>
      <Sec icon="sliders" title={t('po.whatIf')} sub={t('po.whatIfSub')} />

      <div className="pf-plan-strip">
        <div className="pf-plan-strip-head">
          <span className="pf-plan-strip-tag"><Icon name="sparkle" size={14} />{t('po.todaysPlanBase')}</span>
          <Button size="sm" variant="secondary" icon="pencil" onClick={() => setPlanOpen(true)} data-help={t('helpTopics.scenario.body')} data-help-title={t('helpTopics.scenario.title')}>{t('po.editTodaysPlan')}</Button>
        </div>
        <div className="pf-plan-strip-grid">
          <div className="pf-plan-item"><span className="pf-plan-item-l">{t('po.netWealthToday')}</span><span className="pf-plan-item-v">{fmt(startNet)}</span></div>
          <div className="pf-plan-item"><span className="pf-plan-item-l">{t('po.monthlySavingShort')}</span><span className="pf-plan-item-v">{fmt(plan.monthlySave)}</span></div>
          <div className="pf-plan-item"><span className="pf-plan-item-l">{t('po.extraDebtRepayment')}</span><span className="pf-plan-item-v">{fmt(plan.extraDebt)}</span></div>
          <div className="pf-plan-item"><span className="pf-plan-item-l">{t('sharedText.po.desiredNetWealth')}</span><span className="pf-plan-item-v">{plan.targetNet ? fmt(plan.targetNet) : '—'}</span></div>
          <div className="pf-plan-item"><span className="pf-plan-item-l">{t('po.return')}</span><span className="pf-plan-item-v">{String(plan.ret).replace('.', ',')} %</span></div>
          <div className="pf-plan-item"><span className="pf-plan-item-l">{t('po.retirementAge')}</span><span className="pf-plan-item-v">{plan.retireAge} {t('sharedText.sd.transition.years')}</span></div>
        </div>
      </div>

      <div className="po-sim-scen" style={{ marginTop: 16 }}>
        {PF_SCEN.map((item) => <button key={item.key} className={`po-scen${scenario === item.key ? ' on' : ''}`} onClick={() => { setScenario(item.key); if ((item.fields || []).length) setScenOpen(true); }}>
          <Icon name={item.icon} size={16} />{item.label}
        </button>)}
      </div>

      {definition.fields.length > 0 && <div className="pf-scen-active">
        <div className="pf-scen-active-head">
          <span className="pf-scen-active-tag"><Icon name={definition.icon} size={15} />{definition.label}</span>
          <Button size="sm" variant="secondary" icon="sliders" onClick={() => setScenOpen(true)} data-help={t('helpTopics.scenario.body')} data-help-title={t('helpTopics.scenario.title')}>{t('po.adjustAssumptions')}</Button>
        </div>
      </div>}

      <div className="pf-impact-grid">
        {impact.map((item, index) => <div key={index} className="pf-impact">
          <div className="pf-impact-lbl">{item.l}</div>
          <div className="pf-impact-val" style={{ color: PF_TONE[item.tone] }}>{item.v}</div>
        </div>)}
      </div>
      <div className="pf-sim-note"><Icon name="sparkle" size={16} style={{ color: 'var(--plum-700)', flex: 'none' }} /><p>{scen.story()}</p></div>
    </Card>

    <Card>
      <Sec icon="clock" title={t('po.lifeEvents')} sub={t('po.lifeEventsSub')}
        right={<Button size="sm" icon="plus" onClick={() => setEventEdit({ _new: true })} data-help={t('helpTopics.plannedCash.body')} data-help-title={t('helpTopics.plannedCash.title')}>{t('po.addEvent')}</Button>}
        help={t('po.lifeEventsHelp')} />
      {events.length === 0
        ? <div className="pf-life-empty">
          <Icon name="clock" size={22} />
          <p>{t('pkn.fut.noEvents')}</p>
          <Button size="sm" icon="plus" onClick={() => setEventEdit({ _new: true })} data-help={t('helpTopics.plannedCash.body')} data-help-title={t('helpTopics.plannedCash.title')}>{t('po.addEvent')}</Button>
        </div>
        : <div className="pf-life-list">
          {[...events].sort((a, b) => a.start - b.start).map((event) => {
            const inactive = event.active === false;
            const effect5 = Math.round(pfProjectFull(0, 0, 60, plan.ret, [{ ...event, active: true }], 60, 0)[60]);
            return <div key={event.id} className={`pf-life-card${inactive ? ' off' : ''}`}>
              <span className="pf-life-ic" style={{ background: 'var(--panel-2)', color: event.tint || 'var(--plum-600)' }}><Icon name={event.icon || 'sparkle'} size={19} /></span>
              <div className="pf-life-main">
                <div className="pf-life-name">{event.name}</div>
                <div className="pf-life-meta">
                  <span>{pfDateFull(event.start)}</span><span>·</span><span>{event.duration ? t('pkn.fut.forMonths', { count: event.duration }) : t('pkn.fut.permanent')}</span>
                  {!!event.oneTime && <React.Fragment><span>·</span><span>{event.oneTime > 0 ? t('pkn.fut.oneOff', { amount: fmt(event.oneTime) }) : t('pkn.fut.incoming', { amount: fmt(-event.oneTime) })}</span></React.Fragment>}
                  {!!event.monthly && <React.Fragment><span>·</span><span style={{ color: event.monthly < 0 ? 'var(--error)' : 'var(--success)' }}>{event.monthly > 0 ? '+' : ''}{t('pkn.fut.perMonth', { amount: fmt(event.monthly) })}</span></React.Fragment>}
                </div>
              </div>
              <div className="pf-life-eff">
                <span className="pf-life-eff-l">{t('po.effectOver5Years')}</span>
                <span className="pf-life-eff-v" style={{ color: effect5 >= 0 ? 'var(--success)' : 'var(--error)' }}>{effect5 >= 0 ? '+' : '−'}{fmt(Math.abs(effect5))}</span>
              </div>
              <div className="pf-life-tools">
                <button className={`pf-life-toggle${inactive ? '' : ' on'}`} onClick={() => { const next = events.map((item) => (item.id === event.id ? { ...item, active: item.active === false } : item)); setEvents(next); onSavePlanning?.({ lifeEvents: next }); }} title={inactive ? t('pkn.fut.switchOn') : t('pkn.fut.switchOff')} aria-label={inactive ? t('pkn.fut.switchOn') : t('pkn.fut.switchOff')}><span /></button>
                <button className="pf-life-tool" onClick={() => setEventEdit(event)} title={t('cust.act.edit')} aria-label={t('cust.act.edit')}><Icon name="pencil" size={15} /></button>
                <button className="pf-life-tool del" onClick={() => { const next = events.filter((item) => item.id !== event.id); setEvents(next); onSavePlanning?.({ lifeEvents: next }); toast(t('pkn.fut.eventRemoved'), 'info'); }} title={t('cust.chat.remove')} aria-label={t('cust.chat.remove')}><Icon name="trash" size={15} /></button>
              </div>
            </div>;
          })}
        </div>}
    </Card>

    {goals.length > 0 && <Card>
      <Sec icon="checkCircle" title={t('po.goalScore')} sub={t('po.goalScoreSub')} />
      <div className="pf-score-grid">
        <div className="pf-score-ring">
          {window.PoScoreRing && <window.PoScoreRing score={goalScore} />}
          <div className="pf-score-cap">{onTrack} {t('sharedText.transaction.by')} {goals.length} {t('pkn.fut.goalsOnTrackSuffix', { pct: totalPct })}</div>
        </div>
        <div className="pf-score-factors">
          <div className="po-drivers-head" style={{ maxWidth: 'none', textAlign: 'left' }}>{t('po.whatAffectsGoalScore')}</div>
          {factors.map((factor) => <div key={factor.name} className="pf-factor">
            <div className="pf-factor-top"><span className="pf-factor-name">{factor.name}</span><span className="pf-factor-val">{Math.round(factor.pct * 100)}</span></div>
            <div className="pf-factor-track"><div style={{ background: factor.pct >= 0.75 ? 'var(--success)' : factor.pct >= 0.5 ? 'var(--warning)' : 'var(--error)', width: `${factor.pct * 100}%` }} /></div>
            <div className="pf-factor-note">{factor.note}</div>
          </div>)}
        </div>
      </div>
    </Card>}

    {priority && <div className="pf-priority">
      <span className="pf-priority-ava"><img src={pfAvatar()} alt="SuperPlumma" /></span>
      <div className="pf-priority-body">
        <div className="pf-priority-tag"><Icon name="sparkle" size={14} />{t('po.myRecommendationNow')}</div>
        <div className="pf-priority-title">{t('sharedText.po.prioritise')} <b>{priority.goal.name.toLowerCase()}</b> {t('pkn.fut.first')}</div>
        <p>{t('pkn.fut.priorityBody', { amount: fmt(priority.left) })}{priority.onTrack ? ` ${t('pkn.fut.targetDate', { date: pfDateFull(priority.months) })}` : ` ${t('pkn.fut.cannotCalcEnd')}.`}</p>
        {onAsk && <button className="pf-priority-cta" onClick={onAsk}><Icon name="sparkle" size={15} />{t('po.talkAboutPlan')}</button>}
      </div>
    </div>}

    <div>
      <Sec icon="checkCircle" title={t('po.yourGoals')} sub={t('po.yourGoalsSub')}
        right={<Button size="sm" icon="plus" onClick={() => setGoalEdit({ _new: true })} data-help={t('helpTopics.goal.body')} data-help-title={t('helpTopics.goal.title')}>{t('po.newGoal')}</Button>} />
      {goals.length === 0 && <div className="pf-goal-empty">
        <Icon name="sparkle" size={22} />
        <p>{t('pkn.fut.noGoals')}</p>
        <Button size="sm" icon="plus" onClick={() => setGoalEdit({ _new: true })} data-help={t('helpTopics.goal.body')} data-help-title={t('helpTopics.goal.title')}>{t('po.addGoal')}</Button>
      </div>}
      <div className="pf-goal-grid">
        {computedGoals.map((item) => {
          const goal = item.goal;
          const open = openGoal === goal.key;
          return <div key={goal.key} className={`po-goal-card pf-goal${open ? ' open' : ''}`}>
            <div className="po-goal-card-top">
              <div className="po-goal-ring">
                <svg width="88" height="88" viewBox="0 0 88 88">
                  <circle cx="44" cy="44" r="34" fill="none" stroke="var(--panel-2)" strokeWidth="8" />
                  <circle cx="44" cy="44" r="34" fill="none" stroke={goal.tint} strokeWidth="8" strokeLinecap="round"
                    strokeDasharray={`${2 * Math.PI * 34 * (item.pct / 100)} ${2 * Math.PI * 34}`} transform="rotate(-90 44 44)" />
                </svg>
                <span className="po-goal-ring-ico" style={{ color: goal.tint }}><Icon name={goal.icon} size={20} /></span>
              </div>
              <div className="po-goal-card-head">
                <div className="po-goal-name">{goal.name}</div>
                <div className="po-goal-note">{goal.note}</div>
              </div>
              <div className="pf-goal-tools">
                <button className="pf-goal-tool" title={t('po.editGoal')} aria-label={t('po.editGoal')} onClick={() => setGoalEdit(goal)} data-help={t('helpTopics.goal.body')} data-help-title={t('helpTopics.goal.title')}><Icon name="pencil" size={15} /></button>
                <button className="pf-goal-tool del" title={t('po.deleteGoal')} aria-label={t('po.deleteGoal')} onClick={() => setGoalDelete(goal)}><Icon name="trash" size={15} /></button>
              </div>
              <span className="po-goal-pct" style={{ color: goal.tint }}>{item.pct} %</span>
            </div>
            <div className="po-goal-amt">{fmt(goal.saved)} <span>/ {fmt(goal.target)}</span></div>
            <div className="po-goal-track"><div style={{ background: goal.tint, width: `${item.pct}%` }} /></div>
            <div className="pf-goal-stats">
              <div className="pf-gstat"><span className="pf-gstat-l">{t('po.remaining')}</span><span className="pf-gstat-v">{fmt(item.left)}</span></div>
              <div className="pf-gstat"><span className="pf-gstat-l">{t('po.expectedDone')}</span><span className="pf-gstat-v">{item.onTrack ? pfDateFull(item.months) : t('pkn.fut.cannotCalcEnd')}{item.delta > 0 && <span className="pf-gstat-delta">−{item.delta} {t('sharedText.adv.scn.loan.monthsUnit')}</span>}</span></div>
              <div className="pf-gstat"><span className="pf-gstat-l">{t('pkn.fut.planStatus')}</span><span className="pf-gstat-v" style={{ color: item.onTrack ? 'var(--success)' : 'var(--warning)' }}>{item.planStatus}</span></div>
              <div className="pf-gstat"><span className="pf-gstat-l">{t('po.largestEffect')}</span><span className="pf-gstat-v pf-gstat-lever">{goal.lever}</span></div>
            </div>
            <button className="pf-goal-more" onClick={() => setOpenGoal(open ? null : goal.key)}>
              <Icon name="sparkle" size={14} />{t('po.howReachFaster')}<Icon name={open ? 'chevUp' : 'chevDown'} size={15} />
            </button>
            {open && <div className="pf-goal-actions">
              {[t('pkn.fut.action1'), t('pkn.fut.action2'), t('pkn.fut.action3')].map((action, index) => <div key={index} className="pf-goal-action">
                <span className="pf-goal-action-num">{index + 1}</span>
                <span className="pf-goal-action-txt">{action}</span>
              </div>)}
            </div>}
          </div>;
        })}
      </div>
    </div>

    {milestones.length > 0 && <Card>
      <Sec icon="sparkle" title={t('po.nextMilestone')} sub={t('po.nextMilestoneSub')} />
      <div className="pf-mile-list">
        {milestones.map((item, index) => <div key={item.goal.key} className="pf-mile">
          <span className="pf-mile-line"><span className="pf-mile-dot" style={{ background: item.goal.tint }}>{index + 1}</span></span>
          <div className="pf-mile-body">
            <div className="pf-mile-title">{item.goal.nextMark?.label || t('pkn.fut.reachesGoal', { name: item.goal.name })}</div>
            <div className="pf-mile-sub">{item.goal.name} · {t('pkn.fut.amountLeft', { amount: fmt(Math.max(0, (item.goal.nextMark?.at || item.goal.target) - item.goal.saved)) })}</div>
          </div>
          <span className="pf-mile-date">{item.goal.monthly ? pfDateFull(item.markMonths) : '—'}</span>
        </div>)}
      </div>
    </Card>}

    <Card>
      <Sec icon="trendUp" title={t('po.yourLifePlan')} sub={t('po.yourLifePlanSub')} />
      <div className="pf-life">
        {lifePlan.map((entry, index) => <div key={index} className="pf-life-item">
          <div className="pf-life-year">{pfYearOf(entry.months)}</div>
          <div className="pf-life-rail"><span className="pf-life-node" style={{ background: entry.tint }}><Icon name={entry.icon} size={13} /></span>{index < lifePlan.length - 1 && <span className="pf-life-bar" />}</div>
          <div className="pf-life-label">{entry.label}</div>
        </div>)}
      </div>
      <div className="pf-sim-note" style={{ marginTop: 16 }}>
        <Icon name="sparkle" size={16} style={{ color: 'var(--plum-700)', flex: 'none' }} />
        <p>{t('pkn.fut.courseA')}{first ? `, ${first}` : ''}. {t('pkn.fut.courseB')}</p>
      </div>
    </Card>

    {goalEdit && <PoGoalEditor accounts={accounts} goal={goalEdit} onSave={saveGoal} onClose={() => setGoalEdit(null)} />}
    {planOpen && <PoPlanEditor plan={plan} onSave={(next) => { setPlan(next); onSavePlanning?.({ plan: next }); setPlanOpen(false); }} onClose={() => setPlanOpen(false)} />}
    {eventEdit && <PoLifeEventEditor event={eventEdit} onSave={saveEvent} onClose={() => setEventEdit(null)} />}
    {scenOpen && definition.fields.length > 0 && <Modal open onClose={() => setScenOpen(false)} max={560} title={definition.label}
      titleRight={<span className="pf-scen-modal-tag"><Icon name={definition.icon} size={15} /></span>}
      footer={<Button onClick={() => setScenOpen(false)}>{t('clo.done')}</Button>}>
      <div className="pf-scen-modal">
        <div className="pf-plan-info"><Icon name="sparkle" size={16} /><p>{t('po.adjustLive')}</p></div>
        <PoScenControls definition={definition} params={params} onChange={setParam} />
        {scen.stats.length > 0 && <div className="pf-scen-stats">
          {scen.stats.map((stat, index) => <div key={index} className="pf-scen-stat"><span className="pf-scen-stat-l">{stat.l}</span><span className="pf-scen-stat-v" style={{ color: stat.tone ? PF_TONE[stat.tone] : 'var(--ink)' }}>{stat.v}</span></div>)}
        </div>}
        <div className="pf-sim-note" style={{ marginTop: 4 }}><Icon name="sparkle" size={16} style={{ color: 'var(--plum-700)', flex: 'none' }} /><p>{scen.story()}</p></div>
      </div>
    </Modal>}
    {goalDelete && <Modal open onClose={() => setGoalDelete(null)} max={420} title={t('po.deleteGoalTitle')}
      footer={<React.Fragment>
        <Button variant="secondary" onClick={() => setGoalDelete(null)}>{t('common.cancel')}</Button>
        <Button variant="danger" onClick={() => { const next = goals.filter((item) => item.key !== goalDelete.key); setGoals(next); onSavePlanning?.({ goals: next }); setGoalDelete(null); toast(t('pkn.fut.goalDeleted'), 'info'); }}>{t('po.deleteGoalConfirm')}</Button>
      </React.Fragment>}>
      <p style={{ color: 'var(--ink-2)', fontSize: 14.5, fontWeight: 600, lineHeight: 1.6, margin: 0 }}>{t('sharedText.po.doYouWantToDelete')} <b>{goalDelete.name}</b>? {t('pkn.fut.deleteGoalBody', { amount: fmt(goalDelete.saved) })}</p>
    </Modal>}
  </React.Fragment>;
}

// ---------------- Familie ----------------
function PoFamilie({ members = [], onAsk, shared = [] }) {
  const Sec = window.PoSecHead;
  const Says = window.PlummaSays;
  if (members.length === 0) {
    return <React.Fragment>
      {Says && <Says action={onAsk ? t('pkn.fam.askSuperPlumma') : null} onAction={onAsk}>
        {t('pkn.fut.familyIntro')}
      </Says>}
      <Card>
        <Sec icon="users" title={t('po.familyFinances')} sub={t('po.ownPartnerSharedSub')} />
        <div className="gj-empty" style={{ marginTop: 14 }}>{t('pkn.fut.noMembers')}</div>
      </Card>
    </React.Fragment>;
  }
  const maxValue = Math.max(1, ...shared.flatMap((row) => [row.you, row.partner]));
  const total = members.reduce((sum, member) => sum + member.value, 0);
  return <React.Fragment>
    <Card>
      <Sec icon="users" title={t('po.familyFinances')} sub={t('po.ownPartnerSharedSub')} />
      <div className="po-fam-grid" style={{ marginTop: 14 }}>
        {members.map((member) => <div key={member.who} className={`po-fam-card ${member.cls || ''}`}>
          <div className="po-fam-top">
            <span className="po-fam-ava" style={{ background: member.avatarBg }}>{member.initials}</span>
            <div><div className="po-fam-name">{member.who}</div><div className="po-fam-role">{member.role}</div></div>
          </div>
          <div className="po-fam-val">{fmt(member.value)}</div>
          <div className="po-fam-sub">{member.sub} {t('pkn.fam.perMonthShort')}</div>
        </div>)}
      </div>
      {shared.length > 0 && <React.Fragment>
        <div className="t-h3" style={{ fontSize: 15, marginBottom: 10 }}>{t('pkn.fut.personalSpend')}</div>
        <div>
          {shared.map((row) => <div key={row.name} className="aii-bench-row">
            <div className="aii-bench-head"><span className="aii-bench-name">{row.name}</span></div>
            <div className="aii-bench-bars">
              <div className="aii-bench-bar"><span className="who">{t('po.you')}</span><div className="aii-bench-track"><div className="aii-bench-fill you" style={{ width: `${row.you / maxValue * 100}%` }} /></div><span className="num">{fmt(row.you)}</span></div>
              <div className="aii-bench-bar"><span className="who muted">{t('po.partner')}</span><div className="aii-bench-track"><div className="aii-bench-fill avg" style={{ width: `${row.partner / maxValue * 100}%` }} /></div><span className="num">{fmt(row.partner)}</span></div>
            </div>
          </div>)}
        </div>
      </React.Fragment>}
      {total > 0 && Says && <Says>{t('sharedText.po.householdSpends')} <b>{fmt(total)}</b> {t(members.length === 1 ? 'pkn.fut.splitOne' : 'pkn.fut.splitMany', { count: members.length })}</Says>}
    </Card>
  </React.Fragment>;
}

Object.assign(window, { PoFamilie, PoFuture });
